import React, { type MouseEvent, type KeyboardEvent as KeyboardEventType, memo } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Checkbox from '@atlaskit/checkbox';
import { useBulkOpsSessionContext } from '@atlassian/jira-bulk-operations-analytics/src/controllers/index.tsx';
import { useBulkOpsEntryPoint } from '@atlassian/jira-bulk-operations-async-core/src/utils.tsx';
import { useFireCheckboxAnalytics } from '@atlassian/jira-software-context-menu/src/controllers/analytics/main/main.tsx';
import { setCheckboxSelectionMode } from '../../../../../state/actions/cards/checkbox-selection-mode/index.tsx';
import { useBacklogDispatch } from '../../../../../state/index.tsx';
import { getCardSelectionAnalyticsInputSource } from '../utils.tsx';
import { cardEmptySelection } from '../../../../../state/actions/cards/selection/index.tsx';

type Props = {
	isSelected: boolean;
	onClick: (
		withCmd: boolean,
		withShift: boolean,
		source: 'mouse' | 'keyboard',
		viaCheckbox?: boolean,
	) => void;
	isDisabled?: boolean;
	shouldShowCheckbox?: boolean;
	label?: string;
	isCheckboxSelectionMode?: boolean;
};

const CheckboxField = ({
	isSelected,
	onClick,
	isDisabled,
	shouldShowCheckbox,
	label,
	isCheckboxSelectionMode,
}: Props) => {
	const dispatch = useBacklogDispatch();
	const sessionAttributes = useBulkOpsSessionContext()?.getFlowAttributes();
	const { entryPointActions } = useBulkOpsEntryPoint();
	const fireCheckboxAnalytics = useFireCheckboxAnalytics();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleKeyDown = (e: KeyboardEventType) => {
		const withCmd =
			true; /* always considered true for checkbox as it is used for multiple selection */
		const withShift = e.shiftKey;

		if (e.key === 'Space') {
			entryPointActions.load();

			if (!isSelected && !isCheckboxSelectionMode) {
				dispatch(cardEmptySelection());
			}

			if (isSelected && !shouldShowCheckbox) {
				dispatch(setCheckboxSelectionMode(true));

				return;
			}

			onClick(withCmd, withShift, 'keyboard', true);
		}
	};

	const handleClickCheckbox = (e: MouseEvent) => {
		const withCmd =
			true; /* always considered true for checkbox as it is used for multiple selection */
		const withShift = e.shiftKey;

		entryPointActions.load();

		if (!isSelected && !isCheckboxSelectionMode) {
			dispatch(cardEmptySelection());
		}

		if (isSelected && !shouldShowCheckbox) {
			dispatch(setCheckboxSelectionMode(true));
			fireCheckboxAnalytics(
				createAnalyticsEvent({
					action: 'changed',
					actionSubject: 'checkbox',
				}),
				'backlogCard',
				{
					isIssueDetailVisible: true,
					numSelectedCards: 1,
					inputSource: getCardSelectionAnalyticsInputSource(withShift, withCmd, true),
					isCheckboxChecked: isSelected,
					bulkOpsSessionID: sessionAttributes?.bulkOpsSessionID,
				},
			);

			return;
		}
		onClick(withCmd, withShift, 'mouse', true);
	};

	return (
		<Checkbox
			isChecked={isSelected && shouldShowCheckbox}
			onKeyDown={handleKeyDown}
			onClick={handleClickCheckbox}
			isDisabled={isDisabled}
			aria-label={label}
		/>
	);
};

export default memo<Props>(CheckboxField);
